import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { auditDataService } from '@/services/audit-data-service';
import type { Audit, AuditActivityLog } from '@/types/asset-audit';
import { AuditStatus, AuditType } from '@/types/asset-audit';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { DemoModeBadge } from '@/components/ui/demo-mode-badge';
import { useRightPanel } from '@/contexts/RightPanelContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Download, FileSpreadsheet, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Zap, Filter, Columns, History, MoreVertical, FileText, RotateCcw, ArrowRight, Eye } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { AuditHistoryFiltersTab } from '@/components/RightPanel/AuditHistoryFiltersTab';
import { AuditHistoryColumnsTab } from '@/components/RightPanel/AuditHistoryColumnsTab';
import { format } from 'date-fns';
import { exportToCSV, exportToExcel, generateFilename, formatDateForExport, type ExportColumn } from '@/lib/export-utils';
import { toast } from 'sonner';
import { DEFAULT_AUDIT_HISTORY_COLUMNS } from '@/lib/audit-history-columns-config';
import { useColumnResize } from '@/hooks/use-column-resize';
import { cn } from '@/lib/utils';
import { ProductTour } from '@/components/ui/product-tour';
import { useTour } from '@/contexts/TourContext';
import { useAuditHistoryTour } from '@/hooks/use-audit-history-tour';

interface SortIconProps {
  readonly columnKey: keyof Audit;
  readonly sortConfig: { key: keyof Audit | null; direction: 'asc' | 'desc' | null };
}

function SortIcon({ columnKey, sortConfig }: SortIconProps) {
  if (sortConfig.key !== columnKey) {
    return null;
  }
  return sortConfig.direction === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

export function AuditHistory(): React.ReactElement {
  const navigate = useNavigate();
  const { modules } = useFeatureSettings();
  const auditsModule = modules?.audits;
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Activity History Modal State
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [selectedAuditForActivity, setSelectedAuditForActivity] = useState<Audit | null>(null);
  const [activityLogs, setActivityLogs] = useState<AuditActivityLog[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Right Panel Context
  const {
    setIsExpanded,
    setActiveTab,
    selectedRowIds,
    setSelectedRowIds,
    visibleColumns: contextVisibleColumns,
    setVisibleColumns,
    columnOrder: contextColumnOrder,
    setColumnOrder,
    tableFilters,
    setTableFilters,
    isPinned,
    isApplyingFilters,
    setPendingFilters
  } = useRightPanel();

  // Product Tour
  const tour = useAuditHistoryTour();
  const { setOnStartTour } = useTour();

  // Column resize hook
  const initialColumnWidths = {
    select: 80,
    auditNumber: 120,
    title: 300,
    type: 180,
    status: 130,
    totalAssets: 120,
    completedAssets: 150,
    completionPercentage: 130,
    actualEndDate: 150,
    createdDate: 150,
    createdByName: 180,
    exceptionCount: 120,
    actions: 80,
  };

  const { columnWidths, resizingColumn, handleMouseDown } = useColumnResize(initialColumnWidths, ['select']);

  // Initialize visible columns from context or defaults
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (!isInitialized) {
      // Always reset columns and filters for Audit History page
      const defaultVisible = DEFAULT_AUDIT_HISTORY_COLUMNS
        .filter(col => col.visible)
        .map(col => col.id);
      setVisibleColumns(defaultVisible);
      setColumnOrder(DEFAULT_AUDIT_HISTORY_COLUMNS.map(col => col.id));
      
      // Set default filters: Completed and Cancelled ONLY
      // This ensures we only show completed/cancelled audits by default
      const defaultStatuses = [AuditStatus.Completed, AuditStatus.Cancelled];
      setTableFilters({
        status: defaultStatuses,
        type: undefined // Clear any type filters
      });
      
      // Also update pending filters to show default filters in the filter UI
      setPendingFilters({
        selectedAuditStatuses: defaultStatuses.map(s => s.toString()),
        selectedAuditTypes: [],
        selectedAuditPriorities: [],
        selectedAuditors: [],
        auditScheduledDateRange: { from: undefined, to: undefined },
        auditCompletedDateRange: { from: undefined, to: undefined },
        completionDateRange: { from: undefined, to: undefined },
        completionMin: undefined,
        completionMax: undefined,
        completionRateMin: undefined,
        completionRateMax: undefined,
        totalAssetsMin: undefined,
        totalAssetsMax: undefined,
        exceptionCountMin: undefined,
        exceptionCountMax: undefined,
        selectedAssignedUsers: [],
        selectedCategories: [],
        selectedStatuses: [],
        selectedLocations: [],
        acquisitionDateRange: { from: undefined, to: undefined },
        acquisitionCostMin: '',
        acquisitionCostMax: '',
        netBookValueMin: '',
        netBookValueMax: '',
        selectedAssignedTo: [],
        selectedDepreciationMethods: [],
        warrantyExpiryDateRange: { from: undefined, to: undefined },
        selectedManufacturers: [],
      });
      
      setIsInitialized(true);
    }
  }, [isInitialized, setVisibleColumns, setColumnOrder, setTableFilters, setPendingFilters]);

  // Use context columns or fall back to defaults
  const visibleColumns = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_AUDIT_HISTORY_COLUMNS.filter(col => col.visible).map(col => col.id);

  const columnOrder = contextColumnOrder.length > 0
    ? contextColumnOrder
    : DEFAULT_AUDIT_HISTORY_COLUMNS.map(col => col.id);

  // Build ordered visible columns for rendering
  const orderedVisibleColumns = columnOrder
    .filter(id => visibleColumns.includes(id))
    .map(id => DEFAULT_AUDIT_HISTORY_COLUMNS.find(col => col.id === id))
    .filter(Boolean) as typeof DEFAULT_AUDIT_HISTORY_COLUMNS;

  const [sortConfig, setSortConfig] = useState<{
    key: keyof Audit | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: 'actualEndDate',
    direction: 'desc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => tour.startTour);
    return () => {
      setOnStartTour(null);
    };
  }, [setOnStartTour, tour.startTour]);

  useEffect(() => {
    async function loadAudits() {
      // Don't load until initialized to ensure default filters are set
      if (!isInitialized) return;
      
      try {
        setLoading(true);
        setError(null);
        setAudits([]);
        
        const result = await auditDataService.getAudits();
        setAudits(result.items);
      } catch (err) {
        console.error('Failed to load audits:', err);
        setError('Failed to load audits. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadAudits();
  }, [isInitialized]); // Reload when initialized (which sets default filters)

  // Filter audits
  const filteredAudits = audits.filter((audit) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        audit.title.toLowerCase().includes(query) ||
        audit.auditNumber.toLowerCase().includes(query) ||
        (audit.description && audit.description.toLowerCase().includes(query));
      if (!matchesSearch) return false;
    }

    // Status filter (from right panel)
    if (tableFilters.status && (tableFilters.status as string[]).length > 0) {
      if (!(tableFilters.status as string[]).includes(audit.status)) return false;
    }

    // Type filter (from right panel)
    if (tableFilters.type && (tableFilters.type as string[]).length > 0) {
      if (!(tableFilters.type as string[]).includes(audit.type)) return false;
    }

    return true;
  });

  // Sort handler - cycles through: unsorted → asc → desc → unsorted
  const handleSort = (key: keyof Audit) => {
    setSortConfig((prevConfig) => {
      // If clicking a different column, start with asc
      if (prevConfig.key !== key) {
        return { key, direction: 'asc' };
      }
      // If same column, cycle through: asc → desc → unsorted
      if (prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (prevConfig.direction === 'desc') {
        return { key: null, direction: null };
      }
      // If unsorted, start with asc
      return { key, direction: 'asc' };
    });
  };

  // Helper function to compare two values for sorting
  const compareValues = (aValue: unknown, bValue: unknown, direction: 'asc' | 'desc'): number => {
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  };

  // Apply sorting to filtered audits
  const sortedAudits = [...filteredAudits].sort((a: any, b: any) => {
    if (!sortConfig.key || !sortConfig.direction) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    return compareValues(aValue, bValue, sortConfig.direction);
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedAudits.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAudits = sortedAudits.slice(startIndex, endIndex);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, tableFilters]);

  // Row selection handler
  const handleSelectionChange = useCallback((rowId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(rowId);
        if (newSet.size === 1 && !isPinned) {
          setIsExpanded(true);
          setActiveTab('actions');
        }
      } else {
        newSet.delete(rowId);
        if (newSet.size === 0 && !isPinned) {
          setIsExpanded(false);
        }
      }
      return newSet;
    });
  }, [setIsExpanded, setActiveTab, setSelectedRowIds, isPinned]);

  // Select all handler
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedAudits.map(audit => audit.id));
      setSelectedRowIds(allIds);
      if (allIds.size > 0 && !isPinned) {
        setIsExpanded(true);
        setActiveTab('actions');
      }
    } else {
      setSelectedRowIds(new Set());
      if (!isPinned) {
        setIsExpanded(false);
      }
    }
  }, [paginatedAudits, setSelectedRowIds, setIsExpanded, setActiveTab, isPinned]);

  // Activity History handlers
  const handleViewActivityHistory = async (audit: Audit) => {
    setSelectedAuditForActivity(audit);
    setActivityModalOpen(true);
    setLoadingActivity(true);
    
    try {
      const logs = await auditDataService.getActivityLogs(audit.id);
      setActivityLogs(logs.items);
    } catch (err) {
      console.error('Failed to load activity logs:', err);
      toast.error('Failed to load activity history');
      setActivityLogs([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleExportActivityLog = () => {
    if (!selectedAuditForActivity || activityLogs.length === 0) return;

    const columns: ExportColumn[] = [
      { key: 'activityType', label: 'Activity Type' },
      { key: 'timestamp', label: 'Timestamp', format: formatDateForExport },
      { key: 'userName', label: 'User' },
      { key: 'description', label: 'Description' },
    ];

    exportToCSV({
      filename: generateFilename(`audit-activity-${selectedAuditForActivity.auditNumber}`, 'csv'),
      columns,
      data: activityLogs,
    });

    toast.success('Activity log exported successfully');
  };

  // Reopen audit handler
  const handleReopenAudit = async (audit: Audit) => {
    try {
      await auditDataService.reopenAudit(audit.id);
      toast.success('Audit reopened successfully');
      
      // Refresh the audit list
      const result = await auditDataService.getAudits();
      setAudits(result.items);
    } catch (err) {
      console.error('Failed to reopen audit:', err);
      toast.error('Failed to reopen audit');
    }
  };

  const handleExportCSV = () => {
    try {
      const columns: ExportColumn[] = [];
      
      orderedVisibleColumns.forEach(column => {
        switch (column.id) {
          case 'auditNumber':
            columns.push({ key: 'auditNumber', label: 'Audit No.' });
            break;
          case 'title':
            columns.push({ key: 'title', label: 'Audit Title' });
            break;
          case 'type':
            columns.push({ key: 'type', label: 'Type' });
            break;
          case 'status':
            columns.push({ key: 'status', label: 'Status' });
            break;
          case 'totalAssets':
            columns.push({ key: 'totalAssets', label: 'Total Assets' });
            break;
          case 'completedAssets':
            columns.push({ key: 'completedAssets', label: 'Completed Assets' });
            break;
          case 'completionPercentage':
            columns.push({ key: 'completionPercentage', label: 'Completion %', format: (v) => v ? `${Math.floor(v)}%` : '0%' });
            break;
          case 'completedDate':
            columns.push({ key: 'completedDate', label: 'Completed Date', format: formatDateForExport });
            break;
          case 'createdDate':
            columns.push({ key: 'createdDate', label: 'Created Date', format: formatDateForExport });
            break;
          case 'assignedTo':
            columns.push({ key: 'assignedTo', label: 'Assigned To' });
            break;
          case 'createdBy':
            columns.push({ key: 'createdBy', label: 'Created By' });
            break;
          case 'location':
            columns.push({ key: 'location', label: 'Location' });
            break;
          case 'exceptions':
            columns.push({ key: 'exceptions', label: 'Exceptions' });
            break;
        }
      });

      exportToCSV({
        filename: generateFilename('audit-history-export', 'csv'),
        columns,
        data: sortedAudits,
      });

      toast.success(`Exported ${sortedAudits.length} audit(s) to CSV`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportExcel = () => {
    try {
      const columns: ExportColumn[] = [];
      
      orderedVisibleColumns.forEach(column => {
        switch (column.id) {
          case 'auditNumber':
            columns.push({ key: 'auditNumber', label: 'Audit No.' });
            break;
          case 'title':
            columns.push({ key: 'title', label: 'Audit Title' });
            break;
          case 'type':
            columns.push({ key: 'type', label: 'Type' });
            break;
          case 'status':
            columns.push({ key: 'status', label: 'Status' });
            break;
          case 'totalAssets':
            columns.push({ key: 'totalAssets', label: 'Total Assets' });
            break;
          case 'completedAssets':
            columns.push({ key: 'completedAssets', label: 'Completed Assets' });
            break;
          case 'completionPercentage':
            columns.push({ key: 'completionPercentage', label: 'Completion %', format: (v) => v ? `${Math.floor(v)}%` : '0%' });
            break;
          case 'completedDate':
            columns.push({ key: 'completedDate', label: 'Completed Date', format: formatDateForExport });
            break;
          case 'createdDate':
            columns.push({ key: 'createdDate', label: 'Created Date', format: formatDateForExport });
            break;
          case 'assignedTo':
            columns.push({ key: 'assignedTo', label: 'Assigned To' });
            break;
          case 'createdBy':
            columns.push({ key: 'createdBy', label: 'Created By' });
            break;
          case 'location':
            columns.push({ key: 'location', label: 'Location' });
            break;
          case 'exceptions':
            columns.push({ key: 'exceptions', label: 'Exceptions' });
            break;
        }
      });

      exportToExcel({
        filename: generateFilename('audit-history-export', 'xls'),
        sheetName: 'Audit History',
        columns,
        data: sortedAudits,
      });

      toast.success(`Exported ${sortedAudits.length} audit(s) to Excel`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case AuditStatus.Completed:
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
      case AuditStatus.Cancelled:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
      case AuditStatus.InProgress:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case AuditStatus.Draft:
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case AuditStatus.Assigned:
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  const getTypeBadgeStyle = (type: string) => {
    switch (type) {
      case AuditType.FullInventory:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case AuditType.LocationVerification:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case AuditType.ConditionAssessment:
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case AuditType.SpotCheck:
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
      case AuditType.Reconciliation:
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  const formatTypeLabel = (type: string) => {
    // Convert PascalCase to Title Case with spaces
    return type.replace(/([A-Z])/g, ' $1').trim();
  };

  const formatStatusLabel = (status: string) => {
    // Convert PascalCase to Title Case with spaces
    return status.replace(/([A-Z])/g, ' $1').trim();
  };

  // Helper function to get sort key for a column
  const getSortKeyForColumn = (columnId: string): keyof Audit | null => {
    const sortKeyMap: Record<string, keyof Audit> = {
      auditNumber: 'auditNumber',
      title: 'title',
      type: 'type',
      status: 'status',
      totalAssets: 'totalAssets',
      completedAssets: 'completedAssets',
      completionPercentage: 'completionPercentage',
      actualEndDate: 'actualEndDate',
      createdDate: 'createdDate',
      createdByName: 'createdByName',
      exceptionCount: 'exceptionCount',
    };
    return sortKeyMap[columnId] ?? null;
  };

  // Helper function to render table cell content
  const renderCellContent = (columnId: string, audit: Audit) => {
    switch (columnId) {
      case 'auditNumber':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300">{audit.auditNumber}</TableCell>;
      case 'title':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <button
              onClick={() => navigate(`/audits/${audit.id}`)}
              className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex items-center gap-1 group"
            >
              {audit.title}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </button>
          </TableCell>
        );
      case 'type':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getTypeBadgeStyle(audit.type)}`}>
              {formatTypeLabel(audit.type)}
            </span>
          </TableCell>
        );
      case 'status':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeStyle(audit.status)}`}>
              {formatStatusLabel(audit.status)}
            </span>
          </TableCell>
        );
      case 'totalAssets':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{audit.totalAssets}</TableCell>;
      case 'completedAssets':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{audit.completedAssets}</TableCell>;
      case 'completionPercentage':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <Progress 
                  value={Math.floor(audit.completionPercentage)} 
                  className="h-2"
                />
              </div>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300 tabular-nums whitespace-nowrap">
                {Math.floor(audit.completionPercentage)}%
              </span>
            </div>
          </TableCell>
        );
      case 'actualEndDate':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">{audit.actualEndDate ? format(new Date(audit.actualEndDate), 'dd/MM/yyyy HH:mm') : '-'}</TableCell>;
      case 'createdDate':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">{format(new Date(audit.createdDate), 'dd/MM/yyyy HH:mm')}</TableCell>;
      case 'createdByName':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">{audit.createdByName || '-'}</TableCell>;
      case 'exceptionCount':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">{audit.exceptionCount ?? 0}</TableCell>;
      default:
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">-</TableCell>;
    }
  };

  // Render table content based on loading/error state
  const renderTableContent = () => {
    if (loading || isApplyingFilters) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-medium">Loading audits...</p>
          </div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-medium text-destructive">Error</p>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
            <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
              {/* Checkbox column */}
              <TableHead className="h-12 font-semibold relative" style={{ width: `${columnWidths.select}px` }}>
                <div className="flex items-center justify-center px-3">
                  <Checkbox
                    checked={paginatedAudits.length > 0 && paginatedAudits.every(audit => selectedRowIds.has(audit.id))}
                    onCheckedChange={handleSelectAll}
                  />
                </div>
                {/* Column divider - not resizable for checkbox */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                  <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                </div>
              </TableHead>

              {/* Dynamic columns */}
              {orderedVisibleColumns.map((column) => {
                const sortKey = getSortKeyForColumn(column.id);
                const isSortable = column.sortable && sortKey;
                
                return (
                  <TableHead
                    key={column.id}
                    className="h-12 font-semibold relative"
                    style={{ width: `${columnWidths[column.id as keyof typeof columnWidths]}px` }}
                  >
                    <div className="flex items-center">
                      {isSortable && sortKey ? (
                        <button
                          onClick={() => handleSort(sortKey)}
                          className="flex items-center hover:text-primary transition-colors"
                        >
                          {column.label}
                          <SortIcon columnKey={sortKey} sortConfig={sortConfig} />
                        </button>
                      ) : (
                        <span>{column.label}</span>
                      )}
                    </div>
                    {/* Column divider with resize handle */}
                    {column.resizable && (
                      <div
                        role="separator"
                        className="absolute right-0 top-1/2 -translate-y-1/2 h-6 cursor-col-resize group"
                        onMouseDown={(e) => handleMouseDown(column.id, e.clientX)}
                      >
                        <div className={`w-px h-full transition-colors ${
                          resizingColumn === column.id 
                            ? 'bg-primary' 
                            : 'bg-slate-300 dark:bg-slate-600 group-hover:bg-primary'
                        }`} />
                      </div>
                    )}
                  </TableHead>
                );
              })}

              {/* Actions column */}
              <TableHead className="h-12 font-semibold w-[80px]">
                <span>Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedAudits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={orderedVisibleColumns.length + 2} className="h-24 text-center">
                  <p className="text-muted-foreground">No audits found</p>
                </TableCell>
              </TableRow>
            ) : (
              paginatedAudits.map((audit) => (
                <TableRow
                  key={audit.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors"
                >
                  {/* Checkbox cell */}
                  <TableCell className="px-4 py-3" style={{ width: `${columnWidths.select}px` }}>
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={selectedRowIds.has(audit.id)}
                        onCheckedChange={(checked) => handleSelectionChange(audit.id, checked as boolean)}
                      />
                    </div>
                  </TableCell>

                  {/* Dynamic cells */}
                  {orderedVisibleColumns.map((column) => renderCellContent(column.id, audit))}

                  {/* Actions cell */}
                  <TableCell className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/audits/${audit.id}`)}>
                          <FileText className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewActivityHistory(audit)}>
                          <History className="mr-2 h-4 w-4" />
                          Activity History
                        </DropdownMenuItem>
                        {audit.status === AuditStatus.Cancelled && (
                          <DropdownMenuItem onClick={() => handleReopenAudit(audit)}>
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Reopen Audit
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Render activity logs content based on loading state
  const renderActivityLogsContent = () => {
    if (loadingActivity) {
      return (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">Loading activity history...</p>
        </div>
      );
    }
    if (activityLogs.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">No activity history available</p>
        </div>
      );
    }
    return activityLogs.map((log) => (
      <div key={`activity-${log.timestamp}-${log.activityType}`} className="border rounded-lg p-4 space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
              {log.activityType}
            </span>
            <span className="text-sm text-muted-foreground">
              {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss')}
            </span>
          </div>
        </div>
        <div className="text-sm">
          <span className="font-medium">{log.userName}</span>
        </div>
        <div className="text-sm text-muted-foreground">
          {log.description}
        </div>
      </div>
    ));
  };

  // Action Card Component
  const ActionCard = ({
    icon: Icon,
    title,
    description,
    onClick,
    variant,
    disabled
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    onClick?: () => void;
    variant: 'view' | 'action' | 'delete';
    disabled?: boolean;
  }) => {
    const variantStyles = {
      view: {
        container: 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50',
        icon: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50'
      },
      action: {
        container: 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15',
        icon: 'text-primary dark:text-primary',
        iconBg: 'bg-primary/10 dark:bg-primary/20'
      },
      delete: {
        container: 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50',
        icon: 'text-red-600 dark:text-red-400',
        iconBg: 'bg-red-100 dark:bg-red-900/50'
      }
    };

    const styles = variantStyles[variant];

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'w-full p-[5px] rounded-lg transition-all duration-200 text-left group cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          styles.container
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110',
            styles.iconBg
          )}>
            <Icon className={cn('h-6 w-6', styles.icon)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-sm text-foreground">
                {title}
              </h3>
              <ArrowRight className={cn('h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity', styles.icon)} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  // Custom Actions Tab with Export functionality
  const CustomActionsTab = () => {
    const hasSelection = selectedRowIds.size > 0;

    return (
      <div className="p-4 space-y-3">
        {/* Export All Section - Always visible */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            Export
          </h4>
          <ActionCard
            icon={Download}
            title="Export to CSV"
            description="Download all audit history as CSV file"
            variant="action"
            onClick={handleExportCSV}
          />
          <ActionCard
            icon={FileSpreadsheet}
            title="Export to Excel"
            description="Generate Excel spreadsheet with all data"
            variant="action"
            onClick={handleExportExcel}
          />
        </div>

        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Select one or more audits to see additional actions
            </p>
          </div>
        ) : (
          <>
            {/* Export Selected */}
            <div className="space-y-2 border-t pt-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Export Selected
              </h4>
              <ActionCard
                icon={Download}
                title="Export Selected to CSV"
                description={`Download ${selectedRowIds.size} audit${selectedRowIds.size !== 1 ? 's' : ''} as CSV`}
                variant="action"
                onClick={() => console.log('Export selected to CSV')}
              />
              <ActionCard
                icon={FileSpreadsheet}
                title="Export Selected to Excel"
                description={`Generate Excel file with ${selectedRowIds.size} audit${selectedRowIds.size !== 1 ? 's' : ''}`}
                variant="action"
                onClick={() => console.log('Export selected to Excel')}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  // Calculate visible columns count for badge
  const visibleColumnsForBadge = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_AUDIT_HISTORY_COLUMNS.filter(col => col.visible).map(col => col.id);

  // Define page-specific tabs for the right panel - Following Asset Transaction History Pattern
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'actions',
      label: 'Actions',
      icon: Zap,
      content: <CustomActionsTab />,
      showBadge: true,
    },
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <AuditHistoryFiltersTab />,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <AuditHistoryColumnsTab />,
      badgeCount: visibleColumnsForBadge.length,
    },
  ];

  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col overflow-hidden">
        {/* Page Header - Following Asset Transaction History Pattern */}
        <div className="border-b bg-card p-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <History className="h-6 w-6 text-primary" />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold tracking-tight">Audit History</h1>
                  {auditsModule?.mode === 'demo' && <DemoModeBadge />}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  View and manage completed and cancelled audits
                </p>
              </div>
            </div>
            
            {/* Search Bar - Right aligned */}
            <div className="flex items-center gap-3">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search audits..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Container - Fills remaining space */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {renderTableContent()}

          {/* Pagination Footer */}
          {!loading && !error && sortedAudits.length > 0 && (
            <div className="border-t bg-card p-4 flex items-center justify-between flex-shrink-0">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(endIndex, sortedAudits.length)} of {sortedAudits.length} audits
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Page-specific Right Panel */}
      <RightPanelBase tabs={rightPanelTabs} defaultTab="actions" />

      {/* Activity History Modal */}
      <Dialog open={activityModalOpen} onOpenChange={setActivityModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Activity History</DialogTitle>
            <DialogDescription>
              Complete activity log for {selectedAuditForActivity?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto space-y-4 py-4">
            {renderActivityLogsContent()}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setActivityModalOpen(false)}>
              Close
            </Button>
            <Button onClick={handleExportActivityLog} disabled={activityLogs.length === 0}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Tour */}
      <ProductTour
        steps={tour.tourSteps}
        isOpen={tour.isTourOpen}
        onClose={tour.closeTour}
        onComplete={tour.completeTour}
        onStepChange={(stepIndex) => {
          // Open right panel on step 3 (Export Options - index 2)
          if (stepIndex === 2) {
            setIsExpanded(true);
          }
          // Close right panel on step 4 (Audit History Table - index 3)
          if (stepIndex === 3) {
            setIsExpanded(false);
          }
        }}
      />
    </PageWithRightPanel>
  );
}

export default AuditHistory;
